import type {DictateSession, DictationToken} from "@/utils/chat/dictation";

export type SonioxDictationHandles = {
   stop: () => void;
   cancel: () => void;
};

type StartSonioxDictationOptions = {
   session: DictateSession;
   onPartialResult: (tokens: DictationToken[]) => void;
   onFinished: () => void;
   onError: (status: string, message: string) => void;
};

/**
 * Lazy-loads the Soniox web SDK so `window` is never touched during SSR.
 * Mic permission is checked by the caller before a temp key is minted.
 */
export async function startSonioxDictation({
   session,
   onPartialResult,
   onFinished,
   onError,
}: StartSonioxDictationOptions): Promise<SonioxDictationHandles> {
   const {AudioPermissionError, SonioxClient} = await import("@soniox/client");
   const client = new SonioxClient({
      config: {
         api_key: session.apiKey,
         stt_ws_url: session.wsUrl,
      },
   });

   const recording = client.realtime.record({
      model: session.model,
      language_hints: session.languageHints,
   });

   recording.on("result", (result) => {
      onPartialResult(result.tokens ?? []);
   });
   recording.on("finished", onFinished);
   recording.on("error", (error) => {
      onError(error instanceof AudioPermissionError ? "mic_denied" : "websocket_error", error.message);
   });

   return {
      stop: () => {
         recording.finalize();
         void recording.stop();
      },
      cancel: () => {
         recording.cancel();
      },
   };
}
